import { useDisclosure } from '@chakra-ui/react';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useState } from 'react';

import { DateRange } from '../types';
import { useMonthRangeControl } from './use-month-range-control';

type MonthRangePickerOptions = {
	onChange: (value: DateRange) => void;
	value: DateRange;
	disableMonthIf?: (month: Date, interimValue: DateRange) => boolean;
};

export const useMonthRangePicker = ({ onChange, value, disableMonthIf }: MonthRangePickerOptions) => {
	const popover = useDisclosure();

	// Start from the current year, or the year of the selected start month
	const initialYear = value[0]?.getFullYear() || new Date().getFullYear();
	const [currentYear, setCurrentYear] = useState(initialYear);

	// We store the interim date in state instead of directly calling the onChange handler.
	// This requires the user to click "Apply" to confirm the date selection.
	const [interimValue, setInterimValue] = useState(value);

	// Track the clicked selection state (not including hover)
	const [clickedSelection, setClickedSelection] = useState<DateRange>(value);

	// Custom handler to auto-navigate to end year when range is completed
	const handleInterimValueChange = (newRange: DateRange) => {
		setInterimValue(newRange);
		setClickedSelection(newRange); // Update clicked selection on actual range completion

		// If we have a complete range and end date is in a different year, navigate to it
		const [start, end] = newRange;
		if (start && end && end.getFullYear() !== currentYear) {
			setCurrentYear(end.getFullYear());
		}
	};

	const range = useMonthRangeControl({
		value: clickedSelection, // Use clicked selection for the control
		onChange: handleInterimValueChange,
	});

	// Custom select month handler to track clicked selections
	const handleSelectMonth = (month: any) => {
		if (month) {
			const [currentStart, currentEnd] = clickedSelection;

			// If no start date OR we have a complete range, treat click as new start date
			if (!currentStart || (currentStart && currentEnd)) {
				// Set new start date, clear end date
				const startDate = startOfMonth(month);
				const newSelection: DateRange = [startDate, null];
				setInterimValue(newSelection);
				setClickedSelection(newSelection);
			}
			// If we have start but no end, this will be handled by the range control
		}

		// Always call the range control's select method for proper state management
		range.onSelectMonth(month);
	};

	const onApply = () => {
		popover.onClose();
		const [start, end] = interimValue;
		if (start && !end) {
			// Save the single selected month as both start and end if only one month is selected
			onChange([startOfMonth(start), endOfMonth(start)]);
		} else {
			onChange(interimValue);
		}
	};

	const onOpen = () => {
		popover.onOpen();
		setInterimValue(value);
		setClickedSelection(value);
		// Ensure first click after opening starts a new range selection
		range.reset?.();
		// Reset to the year of the selected start month, or current year
		setCurrentYear(value[0]?.getFullYear() || new Date().getFullYear());
	};

	const setRange = (range: DateRange) => {
		handleInterimValueChange(range);
	};

	const onPrevYear = () => {
		setCurrentYear((prev) => prev - 1);
	};

	const onNextYear = () => {
		const currentDate = new Date();
		// If disableMonthIf function exists, check if next year's January would be disabled
		// If so, don't allow navigation beyond current year
		const nextYear = currentYear + 1;
		const januaryNextYear = new Date(nextYear, 0, 1);
		const maxYear = disableMonthIf?.(januaryNextYear, interimValue) ? currentDate.getFullYear() : currentDate.getFullYear() + 10;

		setCurrentYear((prev) => Math.min(prev + 1, maxYear));
	};

	return {
		interimValue,
		isOpen: popover.isOpen,
		currentYear,
		onApply,
		onClear: () => onChange([null, null]),
		onClose: popover.onClose,
		onOpen,
		onPrevYear,
		onNextYear,
		onHoverMonth: range.onHoverMonth, // Keep hover for visual effects
		onSelectMonth: handleSelectMonth, // Use custom handler
		onClearHover: range.onClearHover, // Clear hover state when mouse leaves
		selectionEnd: range.selectionEnd, // Keep for visual selection in calendar
		selectionStart: range.selectionStart, // Keep for visual selection in calendar
		setRange,
		disableMonthIf,
	};
};
